/**
 * Pure display-time field rules for the quote editor (V1 parity, P06). These
 * encode the conditional read-only / visibility logic that V1 applied in
 * editrecord_quote.pl / formatview_quote.pl, so the tabs can stay dumb.
 */

/** N15 (editrecord_quote.pl:4222,4257; formatview_quote.pl:497-513): a chassis-
 *  fee Days field is read-only (and cleared) when the fee type is a flat rate. */
export function chassisDaysReadOnly(feeType?: string | null): boolean {
  const t = (feeType || '').toUpperCase();
  return t === 'FLAT' || t === 'PER DAY';
}

/** N17 (formatview_quote.pl:1088-1107): the Triaxle flag is locked when the
 *  chassis is already a triaxle, or for a heavy 20' container — except a BBC
 *  rate zone exempts WAREHOUSE quotes (DIRECT quotes are never exempt). */
export function triaxleReadOnly(
  d: { chassisType?: string | null; size?: string | null; lbs?: string | number | null; rateZone?: string | null },
  isDirect: boolean,
): boolean {
  if ((d.chassisType || '').toUpperCase() === 'TRIAXLE') return true;
  const lbs = Number(d.lbs) || 0;
  const size = (d.size || '').trim();
  const rz = (d.rateZone || '').toUpperCase();
  return size === '20' && lbs > 42000 && (rz !== 'BBC' || isDirect);
}

/** N17 (formatview_quote.pl:1088-1107): Swing is locked when the load is a
 *  triaxle on a LOCAL-RAIL zone. */
export function swingReadOnly(
  d: { triaxle?: string | null; rateZone?: string | null },
): boolean {
  return (d.triaxle || '').toUpperCase() === 'Y'
    && (d.rateZone || '').toUpperCase() === 'LOCAL-RAIL';
}

/** N13 (editrecord_quote.pl:4020-4047,4174-4261): is a charge whose description
 *  contains `namePattern` present? Drives reveal-on-charge for the free-time /
 *  chassis-fee sub-inputs. Case-insensitive substring so catalog variants
 *  ("GROUNDED YARD STORAGE", "CHASSIS FEE CONTRACT") still match. */
export function hasCharge(
  charges: ReadonlyArray<{ description?: string | null }> | undefined,
  namePattern: string,
): boolean {
  const p = namePattern.toUpperCase();
  return (charges ?? []).some((c) => (c.description || '').toUpperCase().includes(p));
}

/** A SECURED quote is locked for editing: every field renders read-only EXCEPT
 *  the End Date (V1 keepopenfields="|enddate|", editrecord_quote.pl:604-606).
 *  Gate on the RAW status (quoterevisions.status — only ever UNSECURED|SECURED),
 *  NOT the derived display status (which can read EXPIRED/EXPIRING/ACTIVE/…).
 *  The lock covers fields only; New Revision / Dupe / Send / Save stay available
 *  (so the End Date edit can be made + persisted). */
export function isQuoteSecured(rawStatus?: string | null): boolean {
  return (rawStatus || '').toUpperCase() === 'SECURED';
}

/** Customer / Bill-To (and the account details denormalized from them) live on
 *  the shared masterquote row, so they belong to the whole quote — not a single
 *  revision. They may be re-pointed on the ORIGINAL quote (revision 1) while it
 *  is still editable (unsecured, not ended) — e.g. right after a Dupe, which
 *  lands a new quote number at revision 1, UNSECURED. A later revision
 *  (revisionNumber > 1) keeps the account block read-only (it must not re-point
 *  the whole quote's customer/bill-to). Create mode (no revision yet) counts as
 *  the original. `fieldsEditable` already folds in the secured/ended locks. */
export function isAccountEditable(fieldsEditable: boolean, revisionNumber?: number | null): boolean {
  return fieldsEditable && (revisionNumber ?? 1) <= 1;
}

/** Move Type is editable whenever the quote is normally editable, AND on a
 *  SECURED quote that has NEVER been used on an order. A quote can be secured
 *  by hand before any PRO references it; until an order save stamps
 *  quoterevisions.lastuseddate (`lastUsed` blank on the detail) the Move Type
 *  may still be corrected while the rest of the lane stays locked. Once used,
 *  it locks like every other secured field. An ENDED quote stays fully locked
 *  (extend the end date first). Gate on the SAVED detail, not the draft, so a
 *  Save+reload re-evaluates the lock. Backend mirror: `_UNUSED_SECURED_EXEMPT_DQ`
 *  in quotes_create.apply_save_rules. */
export function isMoveTypeEditable(
  fieldsEditable: boolean,
  secured: boolean,
  lastUsed?: string | null,
  endDate?: string | null,
): boolean {
  if (fieldsEditable) return true;
  return secured && !(lastUsed || '').trim() && !isPastEndDate(endDate);
}

/** N19 (formatview_quote.pl:286-292): a revision whose effective date is in the
 *  future (not yet active) is highlighted. Expects MM/DD/YYYY; anything else
 *  (blank, 0000-00-00) is treated as not-future. `today` is injectable. */
export function isFutureEffective(effective?: string | null, today?: Date): boolean {
  const m = (effective || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return false;
  const eff = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  const base = today ?? new Date();
  const midnight = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  return eff.getTime() > midnight.getTime();
}

/** A new quote's Review Date defaults to this many days after its Effective
 *  Date. Flat (John, 2026-07-29) — deliberately NOT V1's new-direct rule of 60
 *  days for FSC type CURRENT / 30 otherwise. */
export const REVIEW_DATE_OFFSET_DAYS = 30;

/** The Review Date implied by an MM/DD/YYYY effective date: effective +
 *  REVIEW_DATE_OFFSET_DAYS, as MM/DD/YYYY. Blank/zero/unparseable input yields
 *  '' (nothing to derive from). Date arithmetic handles month/year/leap
 *  rollover. */
export function derivedReviewDate(effective?: string | null): string {
  const m = (effective || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return '';
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]) + REVIEW_DATE_OFFSET_DAYS);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

/** True when an MM/DD/YYYY end date is strictly before today — i.e. the quote
 *  has already ended. Empty/unparseable dates are not flagged. */
export function isPastEndDate(endDate?: string | null, today?: Date): boolean {
  const m = (endDate || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return false;
  const end = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  const base = today ?? new Date();
  const midnight = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  return end.getTime() < midnight.getTime();
}
